import { DeviceType } from '../device-api/device-type';
import { identifyDeviceType } from './device-identifier';
const loaderMap = {
    [DeviceType.Ion]: loadIonFactory,
    [DeviceType.Vizio]: loadVizioFactory,
    [DeviceType.Chromecast]: loadChromecastFactory,
    [DeviceType.Vidaa]: loadVidaaFactory,
    [DeviceType.WebMaf]: loadWebmafFactory,
    [DeviceType.Prospero]: loadProsperoFactory,
    [DeviceType.WebOS]: loadWebosFactory,
    [DeviceType.Tizen]: loadTizenFactory,
    [DeviceType.YouView]: loadYouViewFactory,
    [DeviceType.Android]: loadAndroidFactory,
    [DeviceType.XboxOne]: loadUWPFactory,
    [DeviceType.XboxSeries]: loadUWPFactory,
    [DeviceType.Netgem]: loadNetgemFactory,
    [DeviceType.Llama]: loadLlamaFactory,
    [DeviceType.OneSdk]: loadOneSdkFactory,
    [DeviceType.MultiChoiceRdk]: loadMultiChoiceRdkFactory,
    [DeviceType.Xfinity]: loadXfinityFactory,
    [DeviceType.Zenterio]: loadZenterioFactory,
    [DeviceType.Quest]: loadQuestFactory,
    [DeviceType.Kepler]: loadKeplerFactory,
    [DeviceType.Kpn]: loadKpnFactory,
    [DeviceType.Browser]: loadBrowserFactory,
    [DeviceType.WinUI2]: loadWinUI2Factory,
};
/**
 * @public
 */
export function loadDeviceFactory(devicesConfig) {
    const loader = loaderMap[identifyDeviceType()];
    return loader(devicesConfig);
}
async function loadIonFactory() {
    const { IonDeviceFactory } = await import(/* webpackChunkName: "device-ion" */ './ion/ion-device-factory');
    return new IonDeviceFactory();
}
async function loadVizioFactory() {
    const { VizioDeviceFactory } = await import(/* webpackChunkName: "device-vz" */ './vizio/vizio-device-factory');
    return new VizioDeviceFactory();
}
async function loadChromecastFactory() {
    const { ChromecastDeviceFactory } = await import(/* webpackChunkName: "device-ch" */ './chromecast/chromecast-device-factory');
    return new ChromecastDeviceFactory();
}
async function loadVidaaFactory() {
    const { VidaaDeviceFactory } = await import(/* webpackChunkName: "device-vd" */ './vidaa/vidaa-device-factory');
    return new VidaaDeviceFactory();
}
async function loadWebmafFactory() {
    const { WebmafDeviceFactory } = await import(/* webpackChunkName: "device-wm" */ './webmaf/webmaf-device-factory');
    return new WebmafDeviceFactory();
}
async function loadProsperoFactory() {
    const { ProsperoDeviceFactory } = await import(/* webpackChunkName: "device-pr" */ './prospero/prospero-device-factory');
    return new ProsperoDeviceFactory();
}
async function loadWebosFactory() {
    const { WebosDeviceFactory } = await import(/* webpackChunkName: "device-wo" */ './webos/webos-device-factory');
    return new WebosDeviceFactory();
}
async function loadTizenFactory() {
    const { TizenDeviceFactory } = await import(/* webpackChunkName: "device-tz" */ './tizen/tizen-device-factory');
    return new TizenDeviceFactory();
}
async function loadYouViewFactory(devicesConfig) {
    const { YouViewDeviceFactory } = await import(/* webpackChunkName: "device-yv" */ './youview/youview-device-factory');
    return new YouViewDeviceFactory(devicesConfig?.youview);
}
async function loadAndroidFactory() {
    const { AndroidTvDeviceFactory } = await import(/* webpackChunkName: "device-an" */ './android-tv/android-tv-device-factory');
    return new AndroidTvDeviceFactory();
}
async function loadUWPFactory(devicesConfig) {
    if (!devicesConfig?.uwp) {
        return Promise.reject(new Error('Device config missing for device uwp'));
    }
    const { UWPDeviceFactory } = await import(/* webpackChunkName: "device-uwp" */ './uwp/uwp-device-factory');
    return new UWPDeviceFactory(devicesConfig.uwp);
}
async function loadNetgemFactory() {
    const { NetgemDeviceFactory } = await import(/* webpackChunkName: "device-ng" */ './netgem/netgem-device-factory');
    return new NetgemDeviceFactory();
}
async function loadLlamaFactory() {
    const { LlamaDeviceFactory } = await import(/* webpackChunkName: "device-ll" */ './llama/llama-device-factory');
    return new LlamaDeviceFactory();
}
async function loadOneSdkFactory() {
    const { OneSdkDeviceFactory } = await import(/* webpackChunkName: "device-one" */ './onesdk/onesdk-device-factory');
    return new OneSdkDeviceFactory();
}
async function loadMultiChoiceRdkFactory() {
    const { MultiChoiceRdkDeviceFactory } = await import(/* webpackChunkName: "device-mc" */ './multichoice-rdk/multichoice-device-factory');
    return new MultiChoiceRdkDeviceFactory();
}
async function loadXfinityFactory(devicesConfig) {
    if (devicesConfig?.xfinity?.shouldUseFireboltApi) {
        const { XfinityRdkDeviceFactory } = await import(/* webpackChunkName: "device-xf" */ './xfinity-rdk/xfinity-rdk-device-factory');
        return new XfinityRdkDeviceFactory();
    }
    const { XfinityDeviceFactory } = await import(/* webpackChunkName: "device-x" */ './xfinity/xfinity-device-factory');
    return new XfinityDeviceFactory();
}
async function loadZenterioFactory() {
    const { ZenterioDeviceFactory } = await import(/* webpackChunkName: "device-mg" */ './zenterio/zenterio-device-factory');
    return new ZenterioDeviceFactory();
}
async function loadQuestFactory() {
    const { QuestDeviceFactory } = await import(/* webpackChunkName: "device-qu" */ './meta-quest/quest-device-factory');
    return new QuestDeviceFactory();
}
async function loadKeplerFactory() {
    const { KeplerDeviceFactory } = await import(/* webpackChunkName: "device-kpl" */ './kepler/kepler-device-factory');
    return new KeplerDeviceFactory();
}
async function loadKpnFactory() {
    const { KpnRdkDeviceFactory } = await import(/* webpackChunkName: "device-kpn" */ './kpn-rdk/kpn-device-factory');
    return new KpnRdkDeviceFactory();
}
async function loadBrowserFactory() {
    const { BrowserDeviceFactory } = await import(/* webpackChunkName: "device-br" */ './browser/browser-device-factory');
    return new BrowserDeviceFactory();
}
async function loadWinUI2Factory() {
    const { WinUI2DeviceFactory } = await import(/* webpackChunkName: "device-winui2" */ './winui2/winui2-device-factory');
    return new WinUI2DeviceFactory();
}
//# sourceMappingURL=device-loader.js.map